import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Eye,
  Trash2,
  Plus,
  ClipboardCheck,
  X,
  Calendar,
  Clock,
  User,
  Tag,
  Upload,
  Paperclip,
  Loader2,
} from "lucide-react";
import Select from "react-select";
import customSelectStyles from "../../../../components/ui/customSelectStyles";
import { AnimatePresence, motion } from "framer-motion";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
  showInfoDialog,
} from "../../../../components/ConfirmDialog";
import { assignTask } from "../../../../api/hrm/tasks/task_info";
import { getAllEmployeesIsActive } from "../../../../api/hrm/employees/employeeInfo";
import { getTaskTags } from "../../../../api/hrm/tasks/task_tag_info";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const selectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(provided, state)
      : provided),
    minHeight: 48,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: state.isFocused ? "#ef4444" : "#d1d5db",
    backgroundColor: "#fff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#ef4444" : "#9ca3af",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
};

const TaskAssignModal = ({ isOpen, onClose, onSaved }) => {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [workDate, setWorkDate] = useState(dayjs().add(1, "day").format("YYYY-MM-DD"));
  const [workTime, setWorkTime] = useState("17:00");
  const [taskInput, setTaskInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [fileInput, setFileInput] = useState(null);
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFileDisabled] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      fetchTags();
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    try {
      const res = await getAllEmployeesIsActive();
      setEmployeeOptions(
        (Array.isArray(res) ? res : []).map((e) => ({
          value: e.id,
          label: `${e.branch_name || "Không rõ chi nhánh"} — ${e.employee_name}`,
        }))
      );
    } catch (err) {
      console.error("Lỗi tải nhân viên:", err);
      setEmployeeOptions([]);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await getTaskTags();
      setTagList(res || []);
    } catch (err) {
      console.error("Lỗi load tags:", err);
      setTagList([]);
    }
  };

  const handleAddTask = () => {
    if (!selectedEmp) return showErrorToast("Vui lòng chọn nhân viên");
    if (!taskInput.trim()) return showErrorToast("Vui lòng nhập nội dung công việc");

    const newTask = {
      employee_id: selectedEmp.value,
      assigner_id: currentUser.id,
      taskname: taskInput.trim(),
      note: noteInput.trim(),
      work_day: workDate,
      work_time: workTime || null,
      file: fileInput || null,
      tag_ids: selectedTags.map((t) => t.value),
    };

    setTaskList([...taskList, newTask]);
    setTaskInput("");
    setNoteInput("");
    setFileInput(null);
    setSelectedTags([]);
  };

  const handleViewTask = (task) => {
    const empLabel =
      employeeOptions.find((e) => e.value === task.employee_id)?.label ||
      "Không rõ nhân viên";

    const dateText = task.work_day
      ? dayjs(task.work_day).format("DD/MM/YYYY")
      : "Không rõ";
    const timeText = task.work_time
      ? dayjs(`2000-01-01T${task.work_time}`).format("HH:mm")
      : "";

    const htmlContent = `
      <div style="text-align:left; line-height:1.6; font-size:14px;">
        <p><strong>Nhân viên:</strong> ${empLabel}</p>
        <p><strong>Thời gian:</strong> ${dateText} ${timeText ? "• " + timeText : ""}</p>
        <p><strong>Công việc:</strong> ${task.taskname || "—"}</p>
        <p><strong>Ghi chú:</strong> ${task.note || "(Không có)"}</p>
        <p><strong>Tag:</strong> ${
          task.tag_ids?.length
            ? task.tag_ids
                .map((id) => {
                  const tg = tagList.find((t) => t.id === id);
                  return `<span style="padding:2px 6px; background:#fee2e2; color:#b91c1c; margin-right:4px; border-radius:999px; font-size:11px;">${
                    tg?.tag_name || "?"
                  }</span>`;
                })
                .join("")
            : "(Không)"
        }</p>
        <p><strong>File:</strong> ${task.file ? task.file.name : "(Không)"}</p>
      </div>
    `;

    showInfoDialog("Chi tiết công việc", htmlContent);
  };

  const handleRemoveTask = async (index) => {
    const confirm = await showConfirmDialog({
      title: "Xác nhận xóa",
      text: "Bạn có chắc muốn xóa công việc này?",
      confirmText: "Đồng ý",
      cancelText: "Hủy",
    });
    if (confirm.isConfirmed) {
      const updated = [...taskList];
      updated.splice(index, 1);
      setTaskList(updated);
    }
  };

  const handleSave = async () => {
    if (taskList.length === 0) return showErrorToast("Chưa có công việc nào để lưu!");

    try {
      setLoading(true);
      await Promise.all(taskList.map((t) => assignTask(t)));
      showSuccessToast("Giao việc thành công!");
      onSaved?.();
      onClose();
      setTaskList([]);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể lưu danh sách giao việc");
    } finally {
      setLoading(false);
    }
  };

  const parseEmpLabel = (emp) => {
    if (!emp?.label) return { name: "—", branch: "Không rõ chi nhánh" };
    const [branchName, employeeName] = emp.label.split(" — ");
    return {
      name: employeeName || emp.label,
      branch: branchName || "Không rõ chi nhánh",
    };
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Giao việc <span className="text-red-700">cho nhân viên</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Phân công công việc kèm hạn và tài liệu đính kèm
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thông tin giao việc
                  </h3>

                  <div>
                    <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                      <User size={12} className="text-red-500" />
                      Nhân viên được giao <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={employeeOptions}
                      value={selectedEmp}
                      onChange={setSelectedEmp}
                      styles={selectStyles}
                      placeholder="Chọn nhân viên"
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="w-full min-w-[10rem] sm:w-[12rem]">
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Calendar size={12} className="text-red-500" />
                        Ngày kết thúc
                      </label>
                      <input
                        type="date"
                        value={workDate}
                        onChange={(e) => setWorkDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div className="w-full min-w-[9rem] sm:w-[11rem]">
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Clock size={12} className="text-red-500" />
                        Giờ kết thúc
                      </label>
                      <input
                        type="time"
                        value={workTime}
                        onChange={(e) => setWorkTime(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div className="min-w-0 flex-1 basis-full lg:basis-auto">
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Tag size={12} className="text-red-500" />
                        Phân loại (Tag)
                      </label>
                      <Select
                        isMulti
                        styles={selectStyles}
                        options={tagList.map((tag) => ({
                          value: tag.id,
                          label: tag.tag_name,
                        }))}
                        value={selectedTags}
                        onChange={(selected) => {
                          if (!selected) return setSelectedTags([]);
                          if (selected.length > 3) {
                            showErrorToast("Chỉ được chọn tối đa 3 tags!");
                            return;
                          }
                          setSelectedTags(selected);
                        }}
                        placeholder="Chọn tag..."
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>
                      Nội dung công việc <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="VD: Chuẩn bị tài liệu đào tạo"
                      value={taskInput}
                      onChange={(e) => setTaskInput(e.target.value)}
                      maxLength={100}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>Ghi chú</label>
                    <textarea
                      rows={3}
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      maxLength={250}
                      placeholder="Thêm hướng dẫn hoặc yêu cầu chi tiết..."
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                      <Paperclip size={12} className="text-red-500" />
                      File đính kèm
                    </label>
                    <div
                      className={`rounded-2xl border-2 border-dashed p-5 text-center transition ${
                        isFileDisabled
                          ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                          : "cursor-pointer border-gray-300 bg-white hover:border-red-400 hover:bg-red-50/40"
                      }`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (isFileDisabled) return;
                        const file = e.dataTransfer.files?.[0];
                        if (file) setFileInput(file);
                      }}
                    >
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        id="assignFileUpload"
                        className="hidden"
                        onChange={(e) => setFileInput(e.target.files?.[0] || null)}
                        disabled={isFileDisabled}
                      />
                      <label
                        htmlFor="assignFileUpload"
                        className={`inline-flex flex-col items-center gap-2 ${
                          isFileDisabled ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-red-600">
                          <Upload size={18} />
                        </span>
                        <span className="text-sm font-bold text-red-700">
                          Chọn tệp để tải lên
                        </span>
                        <span className="text-[11px] font-medium text-gray-400">
                          PDF hoặc ảnh · kéo thả · tối đa 25 MB
                        </span>
                      </label>

                      {fileInput && (
                        <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                          <Paperclip size={14} className="text-red-500" />
                          <strong className="max-w-[200px] truncate">{fileInput.name}</strong>
                          <span className="text-xs text-gray-400">
                            ({(fileInput.size / 1024 / 1024).toFixed(1)} MB)
                          </span>
                          <button
                            type="button"
                            onClick={() => setFileInput(null)}
                            className="ml-1 text-xs font-bold text-red-600 hover:underline"
                          >
                            Xóa
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddTask}
                      className="inline-flex items-center gap-2 rounded-xl border-2 border-red-600 bg-white px-4 py-2.5 text-xs font-black uppercase tracking-wide text-red-700 shadow-sm transition hover:bg-red-50 active:scale-[0.98]"
                    >
                      <Plus size={16} strokeWidth={2.5} />
                      Thêm vào danh sách
                    </button>
                  </div>
                </section>

                <section>
                  <div className="mb-3 flex items-center gap-2">
                    <h3 className="text-sm font-black uppercase tracking-wide text-gray-700">
                      Danh sách giao việc
                    </h3>
                    <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[11px] font-black text-red-600">
                      {taskList.length}
                    </span>
                  </div>

                  {taskList.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-10 text-center">
                      <ClipboardCheck className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                      <p className="text-sm font-medium text-gray-400">
                        Chưa có công việc nào trong danh sách
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {taskList.map((t, idx) => {
                        const emp = employeeOptions.find((e) => e.value === t.employee_id);
                        const { name, branch } = parseEmpLabel(emp);

                        return (
                          <div
                            key={idx}
                            className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm transition hover:border-red-300"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-xs font-black text-red-600">
                                {String(idx + 1).padStart(2, "0")}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="mb-1 flex flex-wrap items-center gap-2">
                                  <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-600">
                                    {dayjs(t.work_day).format("DD/MM/YYYY")}
                                    {t.work_time ? ` · ${t.work_time}` : ""}
                                  </span>
                                  {t.file && (
                                    <span className="inline-flex items-center gap-1 rounded-lg border border-sky-100 bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700">
                                      <Paperclip size={10} /> File
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm font-bold text-gray-800">{t.taskname}</p>
                                <p className="mt-0.5 text-xs font-medium text-gray-500">
                                  {name}
                                  <span className="text-gray-300"> · </span>
                                  <span className="text-gray-400">{branch}</span>
                                </p>
                                {t.note && (
                                  <p className="mt-1 line-clamp-2 text-xs italic text-gray-400">
                                    {t.note}
                                  </p>
                                )}
                                {t.tag_ids?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {t.tag_ids.map((id) => {
                                      const tg = tagList.find((x) => x.id === id);
                                      return (
                                        <span
                                          key={id}
                                          className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-bold text-gray-500"
                                        >
                                          {tg?.tag_name || "—"}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="flex shrink-0 gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleViewTask(t)}
                                  title="Xem chi tiết"
                                  className="rounded-xl border border-gray-200 p-2 text-gray-400 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                                >
                                  <Eye size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTask(idx)}
                                  title="Xóa"
                                  className="rounded-xl border border-gray-200 p-2 text-gray-400 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || taskList.length === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    `Lưu ${taskList.length > 0 ? `(${taskList.length})` : ""} công việc`
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TaskAssignModal;
